import { useTour } from "../../context/TourContext.js"
import { tourSteps } from "../../utils/tutorialSteps.js"
import { useSelector } from "react-redux"
import { TutorialPopover } from "./index.2.0.jsx"
import { useState, useEffect } from "react"
import { useFeatureLock } from "../../hooks/useFeatureLock.js"
import { useMediaQuery } from "@custom-react-hooks/use-media-query"
import { useLocation, useNavigate } from "react-router-dom"
import { useOnboardingStep } from "../../hooks/useOnboardingStep.js"
import { setShowTextReminder } from "../../state/index.js"
import { useDispatch } from "react-redux"
import { useHintProgress } from "../../hooks/useHintProgress.js"
import api from "../../api.js"

const pathMatches = (pattern, pathname) => {
    if (!pattern) return false
    const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$')
    return regex.test(pathname)
}

// path can now be a single string or an array of strings (a step valid on multiple pages)
const matchesAnyPath = (patterns, pathname) => {
    if (!patterns) return false
    const list = Array.isArray(patterns) ? patterns : [patterns]
    return list.some(p => pathMatches(p, pathname))
}

const EXCLUDED_PATHS = [
    '/',
    '/signup',
    '/login',
    '/faq',
    '/privacy-policy',
    '/checkout',
    '/terms-of-service',
    '/commerce-disclosure',
    '/verify-reset-password',
    '/reset-new-password',
    '/report-compromise',
    '/security-password-reset',
    '/subscription-renew',
    '/pre-onboarding',
    '/payment-status',
    '/account-deleted',
    '/admin-nastya',
]

export const TourController = () => {
    const [confirming, setConfirming] = useState(false)
    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

    const isDesktop = useMediaQuery(process.env.REACT_APP_IS_DESKTOP);
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { updateOnboardingStep } = useOnboardingStep()

    const { getEntry, subscribe } = useTour()
    const [, forceTick] = useState(0)

    useEffect(() => {
        return subscribe(() => forceTick(t => t + 1))
    }, [subscribe])

    const onboardingStep = useSelector(state => state.onboardingStep ?? "welcome")
    const { setHintProgress } = useHintProgress()
    const dayNumber = useSelector(state => state.loginDay)
    const { chatLocked } = useFeatureLock()

    const stepIndex = tourSteps.findIndex(s => s.id === onboardingStep)
    const currentStep = tourSteps[stepIndex]

    const conditionMet = useSelector(state => currentStep?.condition ? currentStep.condition(state) : true)

    const lockedHidden = (onboardingStep === "chat-page" && chatLocked)
    const isSystemPage = EXCLUDED_PATHS.some(p => location.pathname === p)

    // skip steps whose excludePath matches the current location
    useEffect(() => {
        if (!currentStep || lockedHidden || isSystemPage) return

        if (currentStep.excludePath && pathMatches(currentStep.excludePath, location.pathname)) {
            const next = tourSteps[stepIndex + 1]
            updateOnboardingStep(next?.id ?? "onboarding-complete")
        }
    }, [onboardingStep, location.pathname, lockedHidden])

    // NOTE: automatic "correction" navigation removed on purpose.
    // The popover now only shows/hides based on whether the user happens to be
    // on one of the step's valid paths. Navigation between pages only happens
    // via handleNext (or the user browsing there themselves).
    // Auto-navigation effect with fallback support
    useEffect(() => {
        if (!currentStep || isSystemPage || lockedHidden) return

        const { path, fallbackPath, showReminder, noNavigation } = currentStep
        if (!path || noNavigation) return

        const isCurrentlyOnValidPath = matchesAnyPath(path, location.pathname)
        const isCurrentlyOnFallback = fallbackPath && pathMatches(fallbackPath, location.pathname)

        // If the user isn't on a valid step path AND isn't already on the fallback page:
        if (!isCurrentlyOnValidPath && !isCurrentlyOnFallback) {
            const hasDynamicSegment = (Array.isArray(path) ? path : [path]).some(p => p.includes(':'))
            if (hasDynamicSegment && fallbackPath) {
                if (showReminder) dispatch(setShowTextReminder(true))
                navigate(fallbackPath)
            } else if (!hasDynamicSegment) {
                const targetPath = Array.isArray(path) ? path[0] : path
                navigate(targetPath)
            }
        }
    }, [onboardingStep, location.pathname, lockedHidden, currentStep, navigate])

    const hidePopover = () => {
        return !currentStep || isSystemPage ||
            lockedHidden ||
            onboardingStep === "onboarding-complete" ||
            !conditionMet ||
            (currentStep.path && !matchesAnyPath(currentStep.path, location.pathname)) ||
            (currentStep.excludePath && pathMatches(currentStep.excludePath, location.pathname))
    }
    if (hidePopover()) return null

    const entry = getEntry(currentStep.target)
    if (!entry) return null

    const anchorRef = { current: entry.el }


    

    const handleNext = async (inputValue) => {
        entry.onNext?.()

        if (currentStep.requiresInput && inputValue?.trim()) {
            try {
                await api.post("/auth/save-goal", { goal: inputValue })
            } finally {
                if (currentStep.confirmationContent) {
                    setConfirming(true)
                    await wait(2500) // show confirmation for 2.5s
                    setConfirming(false)
                }
            }
        }

        const next = tourSteps[stepIndex + 1]
        updateOnboardingStep(next?.id ?? "onboarding-complete")
        if (next?.id === "chat-page") setHintProgress("lastNudgeShownDay", dayNumber)

        if (next?.path && !matchesAnyPath(next.path, location.pathname)) {
            const targetPath = Array.isArray(next.path) ? next.path[0] : next.path
            navigate(targetPath)
        }
    }

    const resolveResponsive = (value, fallback) => {
        if (Array.isArray(value)) {
            const [mobile, desktop] = value
            const resolved = isDesktop ? desktop : mobile
            return resolved !== undefined ? resolved : fallback
        }
        return value !== undefined ? value : fallback
    }

    return (
        <TutorialPopover
            key={onboardingStep}
            anchorRef={anchorRef}
            description={confirming ? currentStep.confirmationContent : currentStep.description}
            hideButtons={confirming ?? false}
            dismissable={currentStep.dismissable ?? false}
            placement={resolveResponsive(currentStep.placement, "top")}
            spotlightPadding={resolveResponsive(currentStep.padding, 8)}
            spotlightMode={resolveResponsive(currentStep.spotlightMode, "spotlight")}
            className={resolveResponsive(currentStep.className, "")}
            hideArrow={resolveResponsive(currentStep.hideArrow, false)}
            top={resolveResponsive(currentStep.top, 0)}
            bottom={resolveResponsive(currentStep.bottom, 0)}
            right={resolveResponsive(currentStep.right, 0)}
            left={resolveResponsive(currentStep.left, 0)}
            disableScroll={resolveResponsive(currentStep.disableScroll, false)}
            delay={resolveResponsive(currentStep.delay, 600)}
            onNext={handleNext}
            currentStep={resolveResponsive(currentStep.currentStep, 0)}
            totalSteps={resolveResponsive(currentStep.totalSteps, 5)}
            buttonContent={resolveResponsive(currentStep.buttonContent, null)}
            hideProgress={resolveResponsive(currentStep.hideProgress, false)}
            allowSkip={resolveResponsive(currentStep.allowSkip, false)}
            requiresInput={currentStep.requiresInput ?? false}
        />
    )
}

// import { useTour } from "../../context/TourContext.js"
// import { tourSteps } from "../../utils/tutorialSteps.js"
// import { useSelector } from "react-redux"
// import { TutorialPopover } from "./index.2.0.jsx"
// import { useState, useEffect } from "react"
// import { useFeatureLock } from "../../hooks/useFeatureLock.js"
// import { useMediaQuery } from "@custom-react-hooks/use-media-query"
// import { useLocation, useNavigate } from "react-router-dom"
// import { useOnboardingStep } from "../../hooks/useOnboardingStep.js"

// const pathMatches = (pattern, pathname) => {
//     if (!pattern) return false
//     const regex = new RegExp('^' + pattern.replace(/:[^/]+/g, '[^/]+') + '$')
//     return regex.test(pathname)
// }

// const isDynamicPath = (pattern) => pattern?.includes(':')

// const findChapterStart = (stepIndex) => {
//     for (let i = stepIndex; i >= 0; i--) {
//         if (tourSteps[i].chapterBeginning) return i
//     }
//     return 0
// }
// const EXCLUDED_PATHS = [
//     '/',
//     '/signup',
//     '/login',
//     '/faq',
//     '/privacy-policy',
//     '/terms-of-service',
//     '/commerce-disclosure',
//     '/verify-reset-password',
//     '/reset-new-password',
//     '/report-compromise',
//     '/security-password-reset',
//     '/subscription-renew',
//     '/pre-onboarding',
//     '/payment-status',
//     '/account-deleted',
//     '/admin-nastya',
// ]

// export const TourController = () => {
//     const isDesktop = useMediaQuery(process.env.REACT_APP_IS_DESKTOP);
//     const location = useLocation()
//     const navigate = useNavigate()
//     const { updateOnboardingStep } = useOnboardingStep()

//     const { getEntry, subscribe } = useTour()
//     const [, forceTick] = useState(0)

//     useEffect(() => {
//         return subscribe(() => forceTick(t => t + 1))
//     }, [subscribe])

//     const onboardingStep = useSelector(state => state.onboardingStep ?? "welcome")
//     const { materialsLocked, chatLocked } = useFeatureLock()

//     const stepIndex = tourSteps.findIndex(s => s.id === onboardingStep)
//     const currentStep = tourSteps[stepIndex]

//     const lockedHidden =
//         (onboardingStep === "materials-page" && materialsLocked) ||
//         (onboardingStep === "chat-page" && chatLocked)
//     const isSystemPage = EXCLUDED_PATHS.some(p => location.pathname === p)

//     // skip steps whose excludePath matches the current location
//     useEffect(() => {
//         if (!currentStep || lockedHidden || isSystemPage) return

//         if (currentStep.excludePath && pathMatches(currentStep.excludePath, location.pathname)) {
//             const next = tourSteps[stepIndex + 1]
//             updateOnboardingStep(next?.id ?? "onboarding-complete")
//         }
//     }, [onboardingStep, location.pathname, lockedHidden])

//     // correct the user's location if it doesn't match the current step's path
//     useEffect(() => {
//         if (!currentStep || lockedHidden || isSystemPage) return
//         if (currentStep.excludePath && pathMatches(currentStep.excludePath, location.pathname)) return

//         if (currentStep.path && !pathMatches(currentStep.path, location.pathname)) {
//             const delay = currentStep.correctionDelay ?? 1000
//             const timer = setTimeout(() => {
//                 if (isDynamicPath(currentStep.path)) {
//                     const chapterStart = tourSteps[findChapterStart(stepIndex)]
//                     if (chapterStart.id !== onboardingStep) {
//                         updateOnboardingStep(chapterStart.id)
//                     }
//                     if (!pathMatches(chapterStart.path, location.pathname)) {
//                         navigate(chapterStart.path)
//                     }
//                 } else if (!["materials-page", "chat-page"].includes(onboardingStep)) {
//                     navigate(currentStep.path)
//                 }
//             }, delay)
//             return () => clearTimeout(timer)
//         }
//     }, [onboardingStep, location.pathname, lockedHidden])

//     const hidePopover = () => {
//         return !currentStep || isSystemPage ||
//             lockedHidden ||
//             onboardingStep === "onboarding-complete" ||
//             (currentStep.path && !pathMatches(currentStep.path, location.pathname)) ||
//             (currentStep.excludePath && pathMatches(currentStep.excludePath, location.pathname))
//     }

//     if (hidePopover()) return null

//     const entry = getEntry(currentStep.target)
//     if (!entry) return null

//     const anchorRef = { current: entry.el }

//     const handleNext = () => {
//         entry.onNext?.()
//         const next = tourSteps[stepIndex + 1]
//         updateOnboardingStep(next?.id ?? "onboarding-complete")
//     }

//     const resolveResponsive = (value, fallback) => {
//         if (Array.isArray(value)) {
//             const [mobile, desktop] = value
//             const resolved = isDesktop ? desktop : mobile
//             return resolved !== undefined ? resolved : fallback
//         }
//         return value !== undefined ? value : fallback
//     }

//     return (
//         <TutorialPopover
//             key={onboardingStep}
//             anchorRef={anchorRef}
//             description={currentStep.description}
//             placement={resolveResponsive(currentStep.placement, "top")}
//             spotlightPadding={resolveResponsive(currentStep.padding, 8)}
//             spotlightMode={resolveResponsive(currentStep.spotlightMode, "spotlight")}
//             className={resolveResponsive(currentStep.className, "")}
//             hideArrow={resolveResponsive(currentStep.hideArrow, false)}
//             top={resolveResponsive(currentStep.top, 0)}
//             bottom={resolveResponsive(currentStep.bottom, 0)}
//             right={resolveResponsive(currentStep.right, 0)}
//             left={resolveResponsive(currentStep.left, 0)}
//             disableScroll={resolveResponsive(currentStep.disableScroll, false)}
//             delay={resolveResponsive(currentStep.delay, 600)}
//             onNext={handleNext}
//             currentStep={resolveResponsive(currentStep.currentStep, 0)}
//             totalSteps={resolveResponsive(currentStep.totalSteps, 5)}
//         />
//     )
// }